import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";

// Import pages
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductPage from "./pages/ProductPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Gallery from "./pages/Gallery";

// Import components
import Footer from "./components/Footer";

function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Fade in on mount
  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
  }, []);

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  // Active link styling
  const linkClass = (path) =>
    `block relative pb-1 transition-all duration-300 hover:text-red-800 hover:underline-offset-4 ${
      location.pathname === path
        ? "text-red-800 font-semibold after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[2px] after:bg-brand-red after:rounded-full"
        : "text-brand-red"
    }`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
        } ${
          scrolled
            ? "bg-white/95 backdrop-blur-none py-3 shadow-lg shadow-gray-300/50"
            : "bg-white/60 backdrop-blur-md py-5 shadow-sm shadow-gray-200/30"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center transition-all duration-500">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center transform transition-transform duration-300 hover:scale-105"
            onClick={() => setMenuOpen(false)}
          >
            <img
              src="/logo.png"
              alt="Texana Trading Logo"
              className={`transition-all duration-500 ${
                scrolled ? "h-10" : "h-12"
              } w-auto`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8 text-sm font-medium">
            <Link to="/" className={linkClass("/")}>
              Home
            </Link>
            <Link to="/products" className={linkClass("/products")}>
              Products
            </Link>
            <Link to="/gallery" className={linkClass("/gallery")}>
              Gallery
            </Link>
            <Link to="/about" className={linkClass("/about")}>
              About
            </Link>
            <Link to="/contact" className={linkClass("/contact")}>
              Contact
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-8 h-8 space-y-1"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block h-1 w-6 bg-brand-red rounded transition-all duration-300 ${
                menuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            ></span>
            <span
              className={`block h-1 w-6 bg-brand-red rounded transition-all duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`block h-1 w-6 bg-brand-red rounded transition-all duration-300 ${
                menuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            ></span>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
            menuOpen
              ? "max-h-96 opacity-100 translate-y-0 visible"
              : "max-h-0 opacity-0 -translate-y-4 invisible"
          } bg-white/95 backdrop-blur-md shadow-lg shadow-gray-300/50`}
        >
          <nav className="flex flex-col items-center py-6 space-y-4 text-lg font-medium">
            <Link
              to="/"
              className={linkClass("/")}
              onClick={() => setMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/products"
              className={linkClass("/products")}
              onClick={() => setMenuOpen(false)}
            >
              Products
            </Link>
            <Link
              to="/gallery"
              className={linkClass("/gallery")}
              onClick={() => setMenuOpen(false)}
            >
              Gallery
            </Link>
            <Link
              to="/about"
              className={linkClass("/about")}
              onClick={() => setMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={linkClass("/contact")}
              onClick={() => setMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>
        </div>
      </header>

      {/* Background Overlay when mobile menu is open */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-500 ${
          menuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setMenuOpen(false)}
      ></div>
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="flex flex-col min-h-screen">
        <Navbar />

        <main className="flex-grow pt-24">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductPage />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;